import { FieldTimeOutlined, UserOutlined } from '@ant-design/icons'
import { Form } from 'antd'
import Paragraph from 'antd/lib/typography/Paragraph'
import { t } from 'i18next'
import { useEffect, useMemo } from 'react'
import Moment from 'react-moment'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  Badge,
  IconTextContainer,
  MultiColumnFormColumnContainer,
  MultiColumnFormContentContainer,
  ReadOnlyFormField,
  SectionLoadingState,
} from '../../../components'
import {
  BadgeColors,
  NO_DATA_PLACEHOLDER,
  dateFormat,
  workspaceDocumentSetStatuses,
  workspaceTypes,
} from '../../../constants/constants'
import { useSearchReviews } from '../../../hooks'
import useWorkspaceDetails from '../../../hooks/useWorkspaceDetails'
import { searchReviewService } from '../../../services'
import RiskReliefDeviationStatisticsPanel from '../components/statistics/RiskReliefDeviationStatisticsPanel'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 124px);
`

interface DeviationDetails {
  historicalReview?: string
  searchQuery?: string
  deviationStatus?: string
  deviationOwner?: string
  deviationLastUpdate?: string
  deviationDataSource?: string
}

const WorkspaceOverviewInfoDeviationPanel = () => {
  const { deviationId } = useParams()
  const { selectedWorkspace } = useWorkspaceDetails({ preventFetch: true })

  const {
    refreshBatchSearchReviewList,
    refreshSearchReviewListDone,
    searchReviewBatchList,
    loading,
  } = useSearchReviews({
    preventFetch: true,
  })

  useEffect(() => {
    const searchReviewIds =
      selectedWorkspace?.config?.riskReliefConfig?.searchReviewIds
    if (!!searchReviewIds?.length && !refreshSearchReviewListDone) {
      refreshBatchSearchReviewList(searchReviewIds)
    }
  }, [
    selectedWorkspace,
    refreshBatchSearchReviewList,
    refreshSearchReviewListDone,
  ])

  const deviationDetails: DeviationDetails | undefined = useMemo(() => {
    const searchQuery = searchReviewService.findSearchQueryInSearchReviewList({
      searchReviewList: searchReviewBatchList,
      searchQueryId: deviationId,
    })
    if (!searchQuery) return undefined

    const parentReview = searchReviewBatchList?.find(
      (review) => review.id === searchQuery.literatureReview
    )

    if (!parentReview) return undefined

    const config =
      selectedWorkspace?.config?.riskReliefConfig?.queryDeviations?.find(
        (deviations) => deviations.searchQueryId === deviationId
      )

    return {
      historicalReview: parentReview.title,
      searchQuery: searchQuery.searchText,
      deviationStatus: config?.deviationStatus || '',
      deviationOwner: searchQuery.searchActionBy.displayName,
      deviationLastUpdate: searchQuery.searchDate,
      deviationDataSource: searchQuery.searchSource.title,
    }
  }, [searchReviewBatchList, selectedWorkspace, deviationId])

  const statisticsContent = useMemo(() => {
    let statistic = null
    switch (selectedWorkspace?.typeCode) {
      default:
        statistic = <RiskReliefDeviationStatisticsPanel />
    }
    return statistic
  }, [selectedWorkspace])

  const canShowStatistics = useMemo(() => {
    return selectedWorkspace?.typeCode === workspaceTypes.RISK_REL
  }, [selectedWorkspace])

  if (loading) {
    return (
      <SectionLoadingStateContainer>
        <SectionLoadingState />
      </SectionLoadingStateContainer>
    )
  }

  return (
    <Form layout="vertical" requiredMark={false}>
      <MultiColumnFormContentContainer>
        <MultiColumnFormColumnContainer>
          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.searchQuery'
            )}
          >
            {deviationDetails?.searchQuery || NO_DATA_PLACEHOLDER}
          </ReadOnlyFormField>
          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.historicalReview'
            )}
          >
            {deviationDetails?.historicalReview || NO_DATA_PLACEHOLDER}
          </ReadOnlyFormField>
          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.dataSource'
            )}
          >
            <Badge color={BadgeColors.DATA_SOURCE}>
              {deviationDetails?.deviationDataSource || NO_DATA_PLACEHOLDER}
            </Badge>
          </ReadOnlyFormField>
          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.status'
            )}
          >
            <Badge
              color={
                deviationDetails?.deviationStatus ===
                workspaceDocumentSetStatuses.OPENED
                  ? BadgeColors.STATUS_OPENED
                  : BadgeColors.STATUS_CLOSED
              }
            >
              {t(
                `workspaceDetailsPage.documentSetStatus.${deviationDetails?.deviationStatus}`
              )}
            </Badge>
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.lastUpdate'
            )}
          >
            <IconTextContainer>
              <FieldTimeOutlined />
              {deviationDetails?.deviationLastUpdate ? (
                <Moment
                  date={deviationDetails?.deviationLastUpdate}
                  format={dateFormat.PRIMARY}
                />
              ) : (
                NO_DATA_PLACEHOLDER
              )}
            </IconTextContainer>
          </ReadOnlyFormField>
          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.owner'
            )}
          >
            <IconTextContainer>
              <UserOutlined />
              {deviationDetails?.deviationOwner || NO_DATA_PLACEHOLDER}
            </IconTextContainer>
          </ReadOnlyFormField>
        </MultiColumnFormColumnContainer>
        {canShowStatistics && (
          <MultiColumnFormColumnContainer>
            <ReadOnlyFormField
              label={t(
                'workspaceDetailsPage.workspaceOverviewInfoPanel.secondDeviationColumnTitle'
              )}
            >
              {selectedWorkspace?.algorithmRuns === undefined ||
              selectedWorkspace?.algorithmRuns?.length === 0 ? (
                <Paragraph
                  style={{
                    paddingLeft: '8px',
                    fontWeight: 400,
                    color: '#434343',
                  }}
                >
                  {t(
                    'workspaceDetailsPage.workspaceOverviewInfoPanel.secondColumnDescription'
                  )}
                </Paragraph>
              ) : (
                statisticsContent
              )}
            </ReadOnlyFormField>
          </MultiColumnFormColumnContainer>
        )}
      </MultiColumnFormContentContainer>
    </Form>
  )
}

export default WorkspaceOverviewInfoDeviationPanel
